import { debug } from './logger'

interface FormTrackerConfig {
  form?: HTMLFormElement
  getTargetWindow: () => Window | null | undefined
}

export class FormTracker {
  private form?: HTMLFormElement
  private getTargetWindow: () => Window | null | undefined
  private isDestroyed: boolean = false
  private abandonmentSent: boolean = false

  private blurHandler: (e: Event) => void
  private pageHideHandler: (e: PageTransitionEvent) => void
  private messageHandler: (e: MessageEvent) => void

  constructor(config: FormTrackerConfig) {
    this.getTargetWindow = config.getTargetWindow

    this.blurHandler = this.handleFieldBlur.bind(this)
    this.pageHideHandler = (e: PageTransitionEvent) => {
      debug('FormTracker pagehide fired, persisted:', e.persisted)
      if (!e.persisted) {
        this.handleAbandonment()
      }
    }
    this.messageHandler = (e: MessageEvent) => {
      if (e.data?.action === 'request-form-contents') {
        debug('FormTracker received request-form-contents, sending all fields')
        this.sendAllFields()
      }
    }

    if (config.form) {
      this.attachForm(config.form)
    }
    window.addEventListener('pagehide', this.pageHideHandler)
    window.addEventListener('message', this.messageHandler)

    debug('FormTracker initialized')
  }

  static create(
    getTargetWindow: () => Window | null | undefined,
    form?: HTMLFormElement
  ): FormTracker {
    return new FormTracker({ form, getTargetWindow })
  }

  setForm(form: HTMLFormElement): void {
    if (this.form) {
      this.form.removeEventListener('blur', this.blurHandler, true)
    }
    this.attachForm(form)
  }

  private attachForm(form: HTMLFormElement): void {
    this.form = form
    this.form.addEventListener('blur', this.blurHandler, true)
    debug('FormTracker attached to form')
  }

  private postMessage(type: string, data?: Record<string, string>): boolean {
    const target = this.getTargetWindow()
    if (!target) {
      debug('FormTracker postMessage skipped — target window not available')
      return false
    }

    target.postMessage({ type, data }, '*')
    return true
  }

  private isTrackableField(
    el: Element
  ): el is HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement {
    if (!('name' in el) || !('value' in el)) {
      return false
    }
    const input = el as HTMLInputElement
    if (!input.name || input.name === 'password') {
      return false
    }
    if (input.type === 'password') {
      return false
    }
    if (input.type === 'radio' && !input.checked) {
      return false
    }
    if (input.hasAttribute('data-enriched')) {
      return false
    }
    return true
  }

  private getFieldValue(el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): string {
    if ((el as HTMLInputElement).type === 'checkbox') {
      return (el as HTMLInputElement).checked ? el.value : ''
    }
    return el.value
  }

  private handleFieldBlur(e: Event): void {
    const target = e.target as Element
    if (!this.isTrackableField(target)) {
      return
    }

    const fieldName = target.name.includes('.') ? target.name.split('.').join('') : target.name
    const fieldValue = this.getFieldValue(target)

    debug('FormTracker blur on field:', fieldName)
    this.postMessage('FORM_FIELD_UPDATE', { fieldName, fieldValue })
  }

  private sendAllFields(): void {
    if (this.isDestroyed || !this.form) {
      return
    }

    const elements = this.form.elements
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i]
      if (!this.isTrackableField(el)) {
        continue
      }

      const fieldName = el.name.includes('.') ? el.name.split('.').join('') : el.name
      const fieldValue = this.getFieldValue(el)
      this.postMessage('FORM_FIELD_UPDATE', { fieldName, fieldValue })
    }
  }

  private hasFilledValues(): boolean {
    if (!this.form) {
      return false
    }
    const elements = this.form.elements
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i]
      if (this.isTrackableField(el) && this.getFieldValue(el) !== '') {
        return true
      }
    }
    return false
  }

  private handleAbandonment(): void {
    if (this.abandonmentSent) {
      debug('FormTracker abandonment already sent, skipping')
      return
    }

    if (!this.hasFilledValues()) {
      debug('FormTracker abandonment skipped — no filled values')
      return
    }

    debug('FormTracker sending abandonment')
    this.abandonmentSent = true
    this.postMessage('FORM_ABANDONED')
  }

  destroy(): void {
    if (this.isDestroyed) return

    this.isDestroyed = true

    this.form?.removeEventListener('blur', this.blurHandler, true)
    window.removeEventListener('pagehide', this.pageHideHandler)
    window.removeEventListener('message', this.messageHandler)

    debug('FormTracker destroyed')
  }
}
